import React, { Fragment, useState, useCallback, useEffect } from 'react';

import { Button, usePermissions, useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { DateTimePicker } from '@material-ui/pickers';
import { TextField, Switch, FormControlLabel, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';

import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import {
  CountryAutocomplete,
  SelectInput,
  SolutionSelect,
  KycLevelSelect,
  AddFilterAction,
  ProfileTagsFilter,
  BranchSelect,
} from '~/components';
import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { Moment } from '~/types';
import { IndividualFilters } from '~/types/individual';
import BackgroundTaskExport from '~/components/BackgroundTaskExport';
import IndividualCreateAction from '../../IndividualCreateAction';
import CustomModule from '~/modules';
import cx from 'classnames';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';

//--------------------------------------------
const resource = 'individuals';

interface Props {
  cleanFilters?: boolean;
}

const Filters = (props: Props) => {
  const { cleanFilters = true } = props;
  const classes = useStyles();
  const listFilterClasses = useListFilterStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const history = useHistory();
  const { permissions } = usePermissions();
  const t = (key: string): string => translate(`resources.individuals.fields.${key}`);
  const hasExtendedSearchPermission = permissions?.includes('individual.search.extended');
  const canViewDrafts =
    permissions?.includes('individual.update') || permissions?.includes('individual.create');

  const filters = useSelector(selectors.getFilters<IndividualFilters>(resource));
  const [localValues, setLocalValues] = useState<IndividualFilters>(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>([]);

  const dispatchFilterChange = (name: string, value: string | Moment) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 500), []);

  const debouncedHandleInput = (e: any) => {
    const { name, value } = e.target;

    setLocalValues({
      ...localValues,
      [name]: value,
    });

    debouncedDispatchFilterChange(name, value);
  };

  const filtersAreEmpty = isEmpty(filters);
  useEffect(() => {
    if (cleanFilters || filtersAreEmpty) {
      dispatch(cleanupFilters({ resource }));
      dispatch(
        filterChange({
          resource,
          filters: {
            withDeleted: false,
          },
        })
      );
      setAddedFilters([]);
    }
  }, [dispatch, cleanFilters, filtersAreEmpty]);

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  useEffect(() => {
    const filterKeyToDisplayKey: Record<string, string> = {
      fromCreatedAt: 'createdAtFrom',
      toCreatedAt: 'createdAtTo',
    };
    const activeDisplayKeys = Object.keys(filters)
      .filter((k) => !!(filters as any)[k])
      .map((k) => filterKeyToDisplayKey[k] || k);

    setAddedFilters((prev) => {
      const merged = new Set([...prev, ...activeDisplayKeys]);
      return Array.from(merged);
    });
  }, [filters]);

  const handleDateTimeChange = (data: Moment, inputType: string) => {
    debouncedDispatchFilterChange(inputType, data);
  };

  const handleSelectInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
    });

    dispatchFilterChange(name, value);
  };

  const hideFilter = (filter: string) => {
    let inputName = filter;
    if (filter === 'createdAtFrom') {
      inputName = 'fromCreatedAt';
    } else if (filter === 'createdAtTo') {
      inputName = 'toCreatedAt';
    }
    dispatchFilterChange(inputName, '');
    setAddedFilters(addedFilters.filter((f) => f !== filter));
  };

  const allFilters = hasExtendedSearchPermission
    ? [
        'primaryDocumentNumber',
        'solution',
        'country',
        'kycLevel',
        'kycStatus',
        'createdAtFrom',
        'createdAtTo',
        'status',
        'affiliationType',
        'affiliatedObjectId',
        'tags',
        'customerNumber',
        'branch',
      ]
    : ['primaryDocumentNumber'];

  const renderAddedFilter = (key: string) => {
    if (hasExtendedSearchPermission) {
      switch (key) {
        case 'primaryDocumentNumber':
          return (
            <TextField
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              label={t('documentNumber')}
              onChange={debouncedHandleInput}
              value={localValues.primaryDocumentNumber ?? ''}
              name='primaryDocumentNumber'
            />
          );
        case 'solution':
          return (
            <SolutionSelect
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              label={t('solutionId')}
              onChange={handleSelectInput}
              value={localValues.solution ?? ''}
              name='solution'
              withFilter={(solution) => solution.features.includes('veengu_individual_profile')}
              allowEmpty
            />
          );
        case 'country':
          return (
            <CountryAutocomplete
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              label={t('country')}
              value={localValues.country}
              name='country'
              onChange={handleSelectInput}
              fullWidth
            />
          );
        case 'kycLevel':
          return (
            <KycLevelSelect
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              label={t('kycLevel')}
              onChange={debouncedHandleInput}
              value={localValues.kycLevel ?? ''}
              name='kycLevel'
              withFeature='veengu_individual_profile'
              allowEmpty
            />
          );
        case 'kycStatus':
          return (
            <SelectInput
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              value={localValues.kycStatus}
              label={t('kycStatus')}
              name='kycStatus'
              onChange={handleSelectInput}
              allowEmpty={false}
              options={[
                { value: '', text: t('any') },
                { value: 'ACTIVE', text: t('active') },
                { value: 'PENDING', text: t('pending') },
              ]}
              defaultValue=''
            />
          );
        case 'createdAtFrom':
          return (
            <DateTimePicker
              className={cx(
                listFilterClasses.formControl,
                listFilterClasses.datePickers,
                listFilterClasses.formControlAdd
              )}
              format='L - LT'
              ampm={hasCurrentLocale12HourFormat()}
              margin='normal'
              label={t('createdAtFrom')}
              value={localValues.fromCreatedAt || null}
              onChange={(date) => handleDateTimeChange(date as Moment, 'fromCreatedAt')}
              clearable
            />
          );
        case 'createdAtTo':
          return (
            <DateTimePicker
              className={cx(
                listFilterClasses.formControl,
                listFilterClasses.datePickers,
                listFilterClasses.formControlAdd
              )}
              format='L - LT'
              ampm={hasCurrentLocale12HourFormat()}
              margin='normal'
              label={t('createdAtTo')}
              value={localValues.toCreatedAt || null}
              onChange={(date) => handleDateTimeChange(date as Moment, 'toCreatedAt')}
              clearable
            />
          );
        case 'status':
          return (
            <SelectInput
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              value={localValues.status}
              label={t('status')}
              name='status'
              onChange={handleSelectInput}
              allowEmpty={false}
              options={[
                { value: '', text: t('any') },
                { value: 'ACTIVE', text: t('active') },
                { value: 'BLOCKED', text: t('blocked') },
                { value: 'CLOSED', text: t('closed') },
              ]}
              defaultValue=''
            />
          );
        case 'affiliationType':
          return (
            <SelectInput
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              value={localValues.affiliationType}
              label={t('affiliationType')}
              name='affiliationType'
              onChange={handleSelectInput}
              allowEmpty={false}
              options={[
                { value: '', text: t('any') },
                { value: 'INVITED_BY', text: t('INVITED_BY') },
                { value: 'CLONED_FROM', text: t('CLONED_FROM') },
              ]}
              defaultValue=''
            />
          );
        case 'affiliatedObjectId':
          return (
            <TextField
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              label={t('affiliatedObjectId')}
              onChange={debouncedHandleInput}
              value={localValues.affiliatedObjectId ?? ''}
              name='affiliatedObjectId'
            />
          );
        case 'tags':
          return (
            <ProfileTagsFilter
              value={localValues.tags}
              className={cx(
                listFilterClasses.formControl,
                listFilterClasses.formControlAdd,
                listFilterClasses.formControlAddLong
              )}
              onChange={(_e: any, value: string[], reason: string) => {
                switch (reason) {
                  case 'create-option':
                  case 'select-option':
                  case 'remove-option':
                    debouncedHandleInput({ target: { name: 'tags', value } });
                    break;
                  case 'clear':
                    debouncedHandleInput({ target: { name: 'tags', value: [] } });
                    break;
                  default:
                    break;
                }
              }}
              resource='individuals'
            />
          );
        case 'branch':
          return (
            <BranchSelect
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              name='branch'
              value={(localValues as any).branch ?? ''}
              onChange={handleSelectInput}
              allowEmpty
            />
          );
        case 'customerNumber':
          return (
            <TextField
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              label={t('customerNumber')}
              onChange={debouncedHandleInput}
              value={localValues.customerNumber ?? ''}
              name='customerNumber'
            />
          );
        default:
          return null;
      }
    } else {
      switch (key) {
        case 'primaryDocumentNumber':
          return (
            <TextField
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              label={t('documentNumber')}
              onChange={debouncedHandleInput}
              value={localValues.primaryDocumentNumber ?? ''}
              name='primaryDocumentNumber'
            />
          );
        default:
          return null;
      }
    }
  };

  const translateFilter = (key: string) => {
    switch (key) {
      case 'primaryDocumentNumber':
        return t('documentNumber');
      case 'solution':
        return t('solutionId');
      default:
        return t(key);
    }
  };

  // Render added filters before the boolean switch
  // to ensure the switch is always at the end of the filter list
  // and to avoid missmathced filter widths
  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (component) {
        return (
          <div className={listFilterClasses.flexContainer} key={filter}>
            {component}
            <IconButton
              aria-label='delete'
              className={listFilterClasses.deleteIcon}
              onClick={() => hideFilter(filter)}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        );
      } else {
        return null;
      }
    });
  return (
    <Fragment>
      {filters && (
        <div className={listFilterClasses.container}>
          <div className={classes.leftSide}>
            {hasExtendedSearchPermission ? (
              <div className={listFilterClasses.inputsGroup}>
                <TextField
                  className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
                  label={t('name')}
                  onChange={debouncedHandleInput}
                  value={localValues.name ?? ''}
                  name='name'
                />
                <TextField
                  className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
                  label={t('phoneNumber')}
                  onChange={debouncedHandleInput}
                  value={localValues.phoneNumber ?? ''}
                  name='phoneNumber'
                  placeholder='+'
                />
                <TextField
                  className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
                  label={t('accountNumber')}
                  onChange={debouncedHandleInput}
                  value={localValues.accountNumber ?? ''}
                  name='accountNumber'
                />
                {renderAddedFilters()}
                <FormControlLabel
                  className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
                  control={
                    <Switch
                      onChange={(e) => {
                        debouncedHandleInput({
                          target: {
                            value: e.target.checked,
                            name: 'withDeleted',
                          },
                        });
                      }}
                      checked={!!localValues.withDeleted}
                      value={localValues.withDeleted ?? ''}
                    />
                  }
                  label={t('withDeleted')}
                />
              </div>
            ) : (
              <div className={listFilterClasses.inputsGroup}>
                <TextField
                  className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
                  label={t('name')}
                  onChange={debouncedHandleInput}
                  value={localValues.name ?? ''}
                  name='name'
                />
                <TextField
                  className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
                  label={t('phoneNumber')}
                  onChange={debouncedHandleInput}
                  value={localValues.phoneNumber ?? ''}
                  name='phoneNumber'
                  placeholder='+'
                />
                <TextField
                  className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
                  label={t('accountNumber')}
                  onChange={debouncedHandleInput}
                  value={localValues.accountNumber ?? ''}
                  name='accountNumber'
                />
                {renderAddedFilters()}
              </div>
            )}
          </div>
          <div className={classes.rightSide}>
            <AddFilterAction
              filters={allFilters
                .filter((f) => !addedFilters.includes(f))
                .sort()
                .map((filterKey) => ({
                  key: filterKey,
                  label: translateFilter(filterKey),
                }))}
              onFilterSelected={(filter: string) => {
                setAddedFilters(addedFilters.concat([filter]));
              }}
            />
            {canViewDrafts && (
              <Button
                label={translate('resources.drafts.myDrafts')}
                onClick={() => history.push('/individuals/drafts')}
              >
                <EditIcon />
              </Button>
            )}
            <IndividualCreateAction />
            <span className={classes.noWrap}>
              <BackgroundTaskExport
                type='INDIVIDUAL_EXPORT'
                queryArguments={handleFilters(localValues)}
              />
            </span>
            <CustomModule name='IndividualImport' />
          </div>
        </div>
      )}
    </Fragment>
  );
};

const handleFilters = (filters: IndividualFilters) => {
  if (!filters) return {};
  return filters;
};

const RIGHT_SIDE_MIN_WIDTH = 300;

const useStyles = makeStyles(() => {
  return {
    leftSide: {
      flex: '0 1 auto',
      minWidth: 0,
    },
    rightSide: {
      flex: '1 1 0',
      minWidth: RIGHT_SIDE_MIN_WIDTH,
      textAlign: 'right',
    },
    noWrap: {
      display: 'inline-block',
      whiteSpace: 'nowrap',
    },
  };
});

export default Filters;
